import WarningIcon from '../../assets/departureWifi/icon_warning.png';
import React, { useState } from 'react';
import styled from 'styled-components';
import IosIpBlocking from './WifiSection/IosIpBlocking';
import IosWifiConnectionCheck from './WifiSection/IosWifiConnectionCheck';
import IosiCloud from './WifiSection/IosiCloud';

type Props = {
  onClose: () => void;
  place?: 'airport' | 'port';
};

function IosWifiResolveModal({ onClose, place = 'airport' }: Props) {
  const [step, setStep] = useState(1);

  const subTitleText =
    step === 1
      ? '1. 와이파이 연결 확인'
      : step === 2
        ? '2. IP차단 해제'
        : '3. iCloud+ 비공개 릴레이 OFF';

  const buttonText = step !== 3 ? '다음' : '확인';

  const onClickNext = () => {
    setStep((prev) => prev + 1);
  };

  return (
    <DimedWindow>
      <Modal>
        <Step step={step}>
          <div aria-current={step === 1} aria-label='Step 1 of 3'></div>
          <div aria-current={step === 2} aria-label='Step 2 of 3'></div>
          <div aria-current={step === 3} aria-label='Step 3 of 3'></div>
        </Step>
        <TitleWrapper>
          <img src={WarningIcon} alt='' width={24} height={24} />
          <Title>와이파이 인증 오류 해결방법</Title>
        </TitleWrapper>
        <SubTitle>{subTitleText}</SubTitle>
        <ContentWrapper step={step}>
          <ErrorStep1 step={step}>
            <IosWifiConnectionCheck place={place} />
            <Description>
              지속적인 네트워크 오류 발생시 ‘문의하기’를 통한 문의 혹은 하단
              버튼을 눌러 항공권 사진을 업로드해주세요.
            </Description>
          </ErrorStep1>
          <ErrorStep2 step={step}>
            <IosIpBlocking place={place} />
            <Description>
              지속적인 네트워크 오류 발생시 ‘문의하기’를 통한 문의 혹은 하단
              버튼을 눌러 항공권 사진을 업로드해주세요.
            </Description>
          </ErrorStep2>
          <ErrorStep3 step={step}>
            <IosiCloud />
            <Description>
              지속적인 네트워크 오류 발생시 ‘문의하기’를 통한 문의 혹은 하단
              버튼을 눌러 항공권 사진을 업로드해주세요.
            </Description>
          </ErrorStep3>
        </ContentWrapper>

        <Footer>
          <Link href='/upload-ticket'>항공권으로 인증하기</Link>
          <ButtonWrapper>
            {step !== 1 && (
              <Button isPrev onClick={() => setStep((prev) => prev - 1)}>
                뒤로
              </Button>
            )}
            <Button onClick={step !== 3 ? onClickNext : onClose}>
              {buttonText}
            </Button>
          </ButtonWrapper>
        </Footer>
      </Modal>
    </DimedWindow>
  );
}

const Step = styled.div<{ step: Number }>`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 16px;

  & > div:nth-child(1) {
    width: ${({ step }) => (step === 1 ? '25px' : '8px')};
    height: 8px;
    border-radius: 50px;
    background-color: ${({ step }) => (step === 1 ? '#A7A7A7' : '#E5E6E8')};
    transition: all 200ms ease-in-out;
  }

  & > div:nth-child(2) {
    width: ${({ step }) => (step === 2 ? '25px' : '8px')};
    height: 8px;
    border-radius: 50px;
    background-color: ${({ step }) => (step === 2 ? '#A7A7A7' : '#E5E6E8')};
    transition: all 200ms ease-in-out;
  }

  & > div:nth-child(3) {
    width: ${({ step }) => (step === 3 ? '25px' : '8px')};
    height: 8px;
    border-radius: 50px;
    background-color: ${({ step }) => (step === 3 ? '#A7A7A7' : '#E5E6E8')};
    transition: all 200ms ease-in-out;
  }
`;

const DimedWindow = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 200;
`;

const Modal = styled.aside`
  position: relative;
  width: 100%;
  max-width: 400px;
  height: 500px;
  margin: 20px;
  padding: 24px;
  flex-direction: column;
  align-items: center;
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ed0828;
`;

const SubTitle = styled.h4`
  font-size: 14px;
  line-height: 21px;
  color: #5f6165;
  margin-bottom: 6px;
`;

const Footer = styled.footer`
  position: absolute;
  bottom: 24px;
  width: calc(100% - 48px);
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Button = styled.button<{ isPrev?: boolean }>`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  background-color: ${({ isPrev = false }) => (isPrev ? '#CBCCCE' : '#246cf6')};
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
`;

const ContentWrapper = styled.div<{ step: number }>`
  display: flex;
  align-items: flex-start;
  transform: translate3d(
    ${({ step }) => (step === 1 ? '0' : step === 2 ? '-100%' : '-200%')},
    0,
    0
  );
  width: 100%;
  transition: transform 200ms ease-in-out;
`;

const ErrorStep1 = styled.div<{ step: number }>`
  width: calc(100vw - 40px 48px);
  flex: 0 0 100%;
  opacity: ${({ step }) => (step === 1 ? '1' : '0')};
  transition: opacity 200ms ease-in-out;
`;

const ErrorStep2 = styled.div<{ step: number }>`
  width: calc(100vw - 40px 48px);
  flex: 0 0 100%;
  opacity: ${({ step }) => (step === 2 ? '1' : '0')};
  transition: opacity 200ms ease-in-out;
`;

const ErrorStep3 = styled.div<{ step: number }>`
  width: calc(100vw - 40px 48px);
  flex: 0 0 100%;
  opacity: ${({ step }) => (step === 3 ? '1' : '0')};
  transition: opacity 200ms ease-in-out;
`;

const Description = styled.p`
  color: #80848a;
  font-size: 12px;
  margin-top: 16px;
  line-height: 18px;
`;

const Link = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 21px;
  color: #246cf6;
  text-decoration: underline;
  margin: 24px 0 16px 0;
`;

export default IosWifiResolveModal;
